import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useLayoutEffect, useRef } from 'react';

import { useResizeObserver } from '@revelio/core';

import { useGridLayout } from '../../hooks/useGridLayout';
import { Chart } from '../Chart/ChartRoot/Chart';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { useResolvedItems } from '../DataResolver/DataResolver';
import gutterStyles from '../grid-gutter.module.css';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_HEIGHT,
} from '../PageConfigProvider/constants';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType, RevelioPageId } from '../PageConfigProvider/types';
import { ReportCoverPage } from '../Reports/ReportCoverPage';
import { ReportFooterPage } from '../Reports/ReportFooterPage';
import ruleStyles from '../Reports/report-page-rule.module.css';
import { ResizableSplitPane } from '../SplitPanel/ResizableSplitPane';
import { scrollPositionStore } from '../scroll-position-store';
import styles from './ContentGrid.module.css';
import { ReportWrapper } from './ReportWrapper';

export const ContentGrid = ({ hidden = false }: { hidden?: boolean }) => {
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const { config, items, revelioPageId } = useTabContentConfig();
  const { pdfExportRef } = usePageExport();
  const resolvedItems = useResolvedItems();
  const { grid, primaryFilter, title, description } = config;

  const gridRows = grid?.rows || DEFAULT_GRID_ROWS;
  const gridColumns = grid?.columns || DEFAULT_GRID_COLUMNS;
  const gridLayout = useGridLayout({
    isReport,
    gridRows,
    gridColumns,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    scrollPositionStore.restore(scrollContainerRef.current);
  }, []);

  const { height: scrollContainerHeight } =
    useResizeObserver(scrollContainerRef);

  const heightPct = (gridRows / DEFAULT_GRID_ROWS) * 100;
  const shouldOverflow =
    (gridRows && gridRows > DEFAULT_GRID_ROWS) || scrollContainerHeight < 560;

  const HeightBase = 100;
  const HeightPrimaryFilter = 42;

  const isTalentDiscovery =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
    revelioPageId === RevelioPageId.POSTING_DISCOVERY;

  const heightDeduction = (() => {
    if (isReport) return 0;
    if (!primaryFilter) return HeightBase;
    return HeightBase + HeightPrimaryFilter;
  })();

  const numPages = Math.floor(gridRows / DEFAULT_GRID_ROWS);
  const pageDividers = Array.from({ length: numPages }).map((_, index) => (
    <hr
      key={index}
      className={classNames(ruleStyles.rule, styles.pageDivider)}
      style={
        {
          '--cg-page-divider-top': `${(index + 1) * REPORT_PAGE_HEIGHT}px`,
        } as CSSProperties
      }
    />
  ));

  return (
    <div
      ref={scrollContainerRef}
      className={classNames(styles.customScrollbar, styles.scrollRoot)}
      // Kept-alive-but-inactive tabs stay mounted and hidden so re-showing them
      // is instant; `display:none` also stops their charts doing layout work.
      data-hidden={hidden || undefined}
      data-report={isReport || undefined}
      data-overflow={shouldOverflow || undefined}
      style={
        {
          '--cg-height': isReport
            ? `calc(${heightPct}% - ${heightDeduction}px)`
            : `calc(100% - ${heightDeduction}px)`,
        } as CSSProperties
      }
      onScroll={(e) => scrollPositionStore.save(e.currentTarget)}
    >
      {isTalentDiscovery ? (
        <ResizableSplitPane items={items} />
      ) : (
        <ReportWrapper isReport={isReport}>
          <div
            ref={pdfExportRef}
            data-report={isReport || undefined}
            className={classNames(
              'content-grid',
              'print-root',
              styles.grid,
              !isReport && styles.dashboardGrid,
              !isReport && gutterStyles.lattice
            )}
            style={
              {
                gridTemplateRows: gridLayout.styles.gridTemplateRows,
                gridTemplateColumns: gridLayout.styles.gridTemplateColumns,
                '--cg-content-height': gridLayout.contentHeight,
                ...(isReport && {
                  '--cg-pad-left': gridLayout.styles.paddingLeft,
                  '--cg-pad-right': gridLayout.styles.paddingRight,
                }),
                '--cg-row-gaps': Math.max(1, gridRows - 1),
                '--cg-col-gaps': Math.max(1, gridColumns - 1),
              } as CSSProperties
            }
          >
            {isReport && (
              <>
                <div className={styles.reportPageSlot}>
                  <ReportCoverPage
                    title={title ?? ''}
                    description={description ?? ''}
                  />
                </div>
                <div
                  className={styles.reportPageSlot}
                  style={
                    {
                      '--cg-page-top': `${(numPages - 1) * REPORT_PAGE_HEIGHT}px`,
                    } as CSSProperties
                  }
                >
                  <ReportFooterPage />
                </div>
              </>
            )}
            {items.map((item) => {
              const { id, position, plot } = item;
              const itemStyle = {
                gridColumn: `${position.x + 1} / span ${position.width}`,
                gridRow: `${position.y + 1} / span ${position.height}`,
              };

              return (
                <div
                  key={id}
                  style={itemStyle}
                  className={classNames(
                    styles.gridItem,
                    !isReport && gutterStyles.cell
                  )}
                  data-raised={
                    (isReport && plot?.color === '#ffffff') || undefined
                  }
                >
                  <Chart
                    item={item}
                    primaryFilter={primaryFilter}
                    data={resolvedItems[id]}
                    {...(!isReport && {
                      headerSize:
                        position.height / gridRows <= 0.23 ? 'sm' : 'md',
                    })}
                    variant={isReport ? 'report' : 'default'}
                  />
                </div>
              );
            })}
            {isReport && pageDividers}
          </div>
        </ReportWrapper>
      )}
    </div>
  );
};
